import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { secondsToMinutes } from 'date-fns';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { MixedItem, RBProgram } from 'store/railsBuilder/reducer.types';
import {
    RecordingItem,
    RecordingMetadata,
} from 'store/recordings/reducer.types';
import { selectLanguage } from 'store/user/selectors';

import { VodEditorial } from 'services/data-manager/types';

import useProgramCountriesStr from 'utils/hooks/useProgramCountriesStr';
import useProgramDateStr from 'utils/hooks/useProgramDateStr';
import {
    isRecordingItem,
    selectRecordingMetadataBaseOnLanguage,
} from 'utils/recordings';

const {
    PROGRAM_DETAILS: { SCREEN_DETAIL_DURATION_MIN },
} = LANGUAGE_KEYS;

function useMetadata(
    item: RBProgram | VodEditorial | MixedItem | RecordingItem | undefined,
    isVod: boolean = false,
) {
    const { t } = useTranslation();
    const currentLanguage = useSelector(selectLanguage);
    const date = useProgramDateStr(item);
    const countries = useProgramCountriesStr(item);

    let genres: RBProgram['Genre'] = [];
    let year: string | number | null | undefined;
    let duration: number | null | undefined;

    const _item = isRecordingItem(item)
        ? selectRecordingMetadataBaseOnLanguage(item.metadata, currentLanguage)
        : item;

    if (_item) {
        genres =
            (_item as RBProgram | VodEditorial | RecordingMetadata).Genre ??
            (_item as MixedItem)?.genres;
        year = (_item as MixedItem).year ?? (_item as RBProgram).Year;

        const vodItem = _item as VodEditorial;

        if (isVod && vodItem.NetDuration != null) {
            duration = secondsToMinutes(
                typeof vodItem.NetDuration === 'string'
                    ? parseInt(vodItem.NetDuration, 10)
                    : vodItem.NetDuration,
            );
        } else {
            duration =
                (_item as RBProgram)?.duration ||
                (_item as RecordingMetadata)?.period?.duration;
        }
    }

    return useMemo(() => {
        let result = '';

        if (year) {
            result += year.toString().concat(' ');
        }

        if (countries) {
            result += countries.concat(' ');
        }

        if (Array.isArray(genres) && genres.length > 0) {
            result += `${genres.slice(0, 2).join(', ')} `;
        }

        if (date && !isVod) {
            result += date;
        }

        if (duration && isVod) {
            result += `${duration}${t(SCREEN_DETAIL_DURATION_MIN)}`;
        }

        return result;
    }, [t, genres, date, countries]);
}

export default useMetadata;
